export const CHAIN_LENGTH_STEP_CM = 5;
export const CHAIN_LENGTH_DEFAULT_CM = 50;
export const CHAIN_LENGTH_FALLBACK_MIN_CM = 50;
export const CHAIN_LENGTH_FALLBACK_MAX_CM = 400;

export interface ChainLengthFieldLike {
  name: string;
  options?: string[];
  /** Варианты, которые видны в калькуляторе. undefined = видны все (legacy). */
  enabledOptions?: string[];
  unit?: string;
  numeric?: string[];
  /** undefined = старое поведение: для длины цепочки ползунок включён. */
  slider?: boolean;
  /** Варианты, при выборе которых калькулятор открывает ползунок. */
  sliderOptions?: string[];
}

export interface ChainLengthRange {
  minCm: number;
  maxCm: number;
  stepCm: number;
  defaultCm: number;
}

/**
 * «Длина цепочки» — единственное поле механизма, которое в калькуляторе
 * показывается непрерывным ползунком, а не набором вариантов.
 */
export function isChainLengthFieldName(name: string): boolean {
  const normalized = name.trim().toLocaleLowerCase('ru-RU').replace(/ё/g, 'е');
  return normalized.includes('длина') && normalized.includes('цепоч');
}

/**
 * Универсальный режим непрерывного выбора для списка механизма.
 * У старого конфига флага нет: только «Длина цепочки» исторически была ползунком.
 */
export function usesMechanismFieldSlider(field: ChainLengthFieldLike): boolean {
  return field.slider ?? isChainLengthFieldName(field.name);
}

/** Варианты, доступные замерщику. Старый конфиг без списка показывает все. */
export function mechanismFieldEnabledOptions(field: ChainLengthFieldLike): string[] {
  const options = field.options ?? [];
  if (field.enabledOptions === undefined) return options;
  return field.enabledOptions.filter((option) => options.includes(option));
}

/** Варианты, при выборе которых обычный ввод заменяется ползунком. */
export function mechanismFieldSliderOptions(field: ChainLengthFieldLike): string[] {
  const enabledOptions = mechanismFieldEnabledOptions(field);
  if (field.sliderOptions) {
    return field.sliderOptions.filter((option) => enabledOptions.includes(option));
  }
  const numeric = (field.numeric ?? []).filter((option) => enabledOptions.includes(option));
  if (numeric.length) return numeric;
  const custom = enabledOptions.find((option) => /кастом|произволь/i.test(option));
  return custom ? [custom] : [];
}

/** Конкретный выбранный вариант открывает ползунок. */
export function optionUsesMechanismFieldSlider(
  field: ChainLengthFieldLike,
  selected: string | null | undefined,
): boolean {
  return !!selected
    && usesMechanismFieldSlider(field)
    && mechanismFieldSliderOptions(field).includes(selected);
}

/** Разбирает длину в сантиметры. Без явной единицы используется unit поля. */
export function parseChainLengthCm(raw: string | null | undefined, unit = 'м'): number | null {
  const normalized = (raw ?? '').trim().toLocaleLowerCase('ru-RU').replace(',', '.');
  const match = normalized.match(/\d+(?:\.\d+)?/);
  if (!match) return null;

  const amount = Number(match[0]);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  if (/(?:мм|mm)/.test(normalized)) return Math.round(amount / 10);
  if (/(?:см|cm)/.test(normalized)) return Math.round(amount);
  if (/(?:м|m)/.test(normalized)) return Math.round(amount * 100);

  const normalizedUnit = unit.trim().toLocaleLowerCase('ru-RU');
  if (/(?:мм|mm)/.test(normalizedUnit)) return Math.round(amount / 10);
  if (/(?:см|cm)/.test(normalizedUnit)) return Math.round(amount);
  return Math.round(amount * 100);
}

const floorToStep = (value: number) => Math.floor(value / CHAIN_LENGTH_STEP_CM) * CHAIN_LENGTH_STEP_CM;
const ceilToStep = (value: number) => Math.ceil(value / CHAIN_LENGTH_STEP_CM) * CHAIN_LENGTH_STEP_CM;

export function snapChainLengthCm(value: number, range: Pick<ChainLengthRange, 'minCm' | 'maxCm'>): number {
  const snapped = Math.round(value / CHAIN_LENGTH_STEP_CM) * CHAIN_LENGTH_STEP_CM;
  return Math.max(range.minCm, Math.min(range.maxCm, snapped));
}

/**
 * Старые конфиги содержат готовые варианты (1.25м, 1.50м, …, 4м): берём
 * их крайние значения как диапазон. Для «Стандарт / Кастомный» числовых
 * границ нет, поэтому используем 0,50–4,00 м. Новое значение начинается с
 * минимальной допустимой длины; сохранённое значение по-прежнему имеет
 * приоритет в chainLengthCmFromState.
 */
export function chainLengthRange(field: ChainLengthFieldLike): ChainLengthRange {
  const configured = [...new Set((field.options ?? [])
    .map((option) => parseChainLengthCm(option, field.unit))
    .filter((value): value is number => value != null))];

  let minCm = CHAIN_LENGTH_FALLBACK_MIN_CM;
  let maxCm = CHAIN_LENGTH_FALLBACK_MAX_CM;
  if (configured.length >= 2) {
    minCm = floorToStep(Math.min(...configured));
    maxCm = ceilToStep(Math.max(...configured));
  } else if (configured.length === 1) {
    minCm = Math.min(minCm, floorToStep(configured[0]!));
    maxCm = Math.max(maxCm, ceilToStep(configured[0]!));
  }

  const range = { minCm, maxCm };
  return {
    ...range,
    stepCm: CHAIN_LENGTH_STEP_CM,
    defaultCm: snapChainLengthCm(CHAIN_LENGTH_DEFAULT_CM, range),
  };
}

/** Текущее значение: новое numeric-value → старый выбранный пресет → дефолт. */
export function chainLengthCmFromState(
  field: ChainLengthFieldLike,
  selected: string | null | undefined,
  value: string | null | undefined,
): number {
  const range = chainLengthRange(field);
  const parsed = parseChainLengthCm(value, field.unit)
    ?? parseChainLengthCm(selected, field.unit)
    ?? range.defaultCm;
  return snapChainLengthCm(parsed, range);
}

/** Храним метры с двумя знаками: один шаг всегда равен 0,05 м. */
export function chainLengthMetersValue(cm: number): string {
  return (cm / 100).toFixed(2);
}

/** Пользовательская и производственная подпись: 1,50 м. */
export function formatChainLengthCm(cm: number): string {
  return `${chainLengthMetersValue(cm).replace('.', ',')} м`;
}
